import {
  FC,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRedactedV2 } from "../../redacted/v2";
import { AccountDataContext } from "../../services/accountData";
import { RedactedScope, useRedacted } from "../../services/redacted";

/**
 * Model B portfolio: one wallet view across every sub that holds a position.
 * Each sub renders the unchanged Rujira tab in its own data scope; only scopes
 * with rows are shown, so the list reads as one account. Acting on a row
 * first scopes the app to that position's sub.
 */
export const PositionScopes: FC<{ children: ReactNode }> = ({ children }) => {
  const v2 = useRedactedV2();
  const { isPrivateMode } = useRedacted();
  // The Spending sub and every position sub (older Spending accounts too),
  // each once, in an order that stays put while a position is managed.
  const subs = v2.modelB.scopes;
  const [filled, setFilled] = useState<Record<string, boolean>>({});
  const report = useCallback(
    (subwallet: string, hasRows: boolean) =>
      setFilled((current) =>
        current[subwallet] === hasRows
          ? current
          : { ...current, [subwallet]: hasRows }
      ),
    []
  );

  if (!v2.modelB.enabled || !isPrivateMode || subs.length === 0)
    return <>{children}</>;

  const anyFilled = subs.some((subwallet) => filled[subwallet]);
  return (
    <>
      {subs.map((subwallet, position) => (
        <Scope
          key={subwallet}
          subwallet={subwallet}
          hidden={anyFilled ? !filled[subwallet] : position > 0}
          onRows={report}
          onAct={() => {
            try {
              v2.modelB.pin(subwallet);
            } catch {
              /* No longer owned; the row stays read-only. */
            }
          }}>
          {children}
        </Scope>
      ))}
    </>
  );
};

const Scope: FC<{
  subwallet: string;
  hidden: boolean;
  onRows: (subwallet: string, hasRows: boolean) => void;
  onAct: () => void;
  children: ReactNode;
}> = ({ subwallet, hidden, onRows, onAct, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const check = () =>
      onRows(subwallet, !!node.querySelector("tbody tr, .orders__row"));
    check();
    const observer = new MutationObserver(check);
    observer.observe(node, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [onRows, subwallet]);
  return (
    <div
      ref={ref}
      className="rd-position-scope"
      style={hidden ? { display: "none" } : undefined}
      onClickCapture={onAct}>
      <RedactedScope subWalletAddress={subwallet}>
        <AccountDataContext>
          <Suspense fallback={null}>{children}</Suspense>
        </AccountDataContext>
      </RedactedScope>
    </div>
  );
};
